import { RiDownload2Line } from 'react-icons/ri'
import { useInView } from '@/hooks'
import { Link } from 'react-router'
import { ROUTES } from '@/constants/routes'

import morningSicknessImg from '@/assets/H.E.R Journey Hub/157742.png'
import keepingActiveImg from '@/assets/H.E.R Journey Hub/157737.png'
import nutritionImg from '@/assets/H.E.R Journey Hub/157739.png'
import maternityLeaveImg from '@/assets/H.E.R Journey Hub/157741.png'
import hospitalBagImg from '@/assets/H.E.R Journey Hub/157740.png'
import breastfeedingImg from '@/assets/H.E.R Journey Hub/157743.png'

const resources = [
  {
    id: 'morning-sickness',
    number: '01',
    title: 'Morning Sickness',
    tagline: 'You are not alone. This is a phase, not forever.',
    description:
      'Morning sickness is very common during pregnancy and can happen at any time of day or night. This guide covers symptoms, practical remedies, what to eat and avoid, and important signs of when to seek medical support.',
    highlights: [
      'Common symptoms and triggers',
      'Practical remedies and what to eat',
      'Warning signs of Hyperemesis Gravidarum',
      'Encouragement and emotional support',
    ],
    colour: 'crimson',
    img: morningSicknessImg,
    filename: 'HER-Morning-Sickness-Guide.png',
  },
  {
    id: 'keeping-active',
    number: '02',
    title: 'Keeping Active & Exercise',
    tagline: 'Light movement. Stronger you. Healthier baby.',
    description:
      'Regular physical activity during pregnancy can improve your mood, boost energy, support healthy weight gain, and prepare your body for labour and birth. This guide covers safe exercises and important warning signs to look out for.',
    highlights: [
      'Safe and effective light activities',
      'Tips for staying active safely',
      'What not to do during pregnancy',
      'Warning signs to watch for when exercising',
    ],
    colour: 'gold',
    img: keepingActiveImg,
    filename: 'HER-Keeping-Active-Guide.png',
  },
  {
    id: 'nutrition',
    number: '03',
    title: 'Nutrition & Food to Eat',
    tagline: 'Nourish you. Grow your baby. Support your journey.',
    description:
      "Eating a balanced, nutrient-rich diet during pregnancy is one of the best things you can do for your health and your baby's development. This guide covers what to eat, what to avoid, hydration, and essential nutrition tips.",
    highlights: [
      'Fruits, vegetables, grains, and protein sources',
      'Dairy, iron-rich, and hydrating foods',
      'Foods to avoid and important reminders',
      'Benefits of eating well for you and baby',
    ],
    colour: 'crimson',
    img: nutritionImg,
    filename: 'HER-Nutrition-Guide.png',
  },
  {
    id: 'maternity-leave',
    number: '04',
    title: 'Planning for Maternity Leave',
    tagline: 'Prepare today. Rest tomorrow. Thrive together.',
    description:
      'Planning ahead helps you transition into motherhood with less stress and more confidence. This guide covers work and career planning, finances, home and family preparation, personal wellbeing, and a step-by-step maternity checklist.',
    highlights: [
      'Work, career, and handover planning',
      'Finances and income preparation',
      'Home, family, and personal wellbeing',
      'Maternity checklist and key dates',
    ],
    colour: 'gold',
    img: maternityLeaveImg,
    filename: 'HER-Maternity-Leave-Guide.png',
  },
  {
    id: 'hospital-bag',
    number: '05',
    title: 'Hospital Bag Check',
    tagline: 'Prepared, confident, and ready to welcome baby.',
    description:
      "Having your hospital bag packed in advance helps you feel calm, organised, and ready for your baby's arrival. This guide covers everything you need for labour, birth, and postpartum — for you, your baby, and your birth partner.",
    highlights: [
      'Clothing, toiletries, and personal care',
      'Important documents and electronics',
      'What to pack for baby',
      'Essential luxury and comfort items',
    ],
    colour: 'crimson',
    img: hospitalBagImg,
    filename: 'HER-Hospital-Bag-Guide.png',
  },
  {
    id: 'breastfeeding',
    number: '06',
    title: 'Breastfeeding',
    tagline: 'Nourish. Bond. Thrive.',
    description:
      "Breastfeeding provides perfect nutrition for your baby and powerful health benefits for both of you. It's also about connection, comfort, and love. This guide covers getting started, common challenges, boosting your milk supply, and how to ask for support.",
    highlights: [
      'Getting started — skin-to-skin and latch',
      'Common challenges and how to manage them',
      'Boosting and maintaining your milk supply',
      'Benefits for both mama and baby',
    ],
    colour: 'gold',
    img: breastfeedingImg,
    filename: 'HER-Breastfeeding-Guide.png',
  },
]

type Resource = (typeof resources)[0]

const ResourceCard = ({ resource, index }: { resource: Resource; index: number }) => {
  const { ref, inView } = useInView<HTMLDivElement>()
  const isGold = resource.colour === 'gold'

  return (
    <div
      ref={ref}
      className={`group relative flex flex-col bg-white rounded-2xl border border-base-100 overflow-hidden shadow-[0_4px_24px_-4px_rgba(26,21,16,0.08)] hover:shadow-[0_8px_32px_-4px_rgba(155,28,28,0.14)] transition-all duration-300 hover:-translate-y-1 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: inView ? `${index * 0.08}s` : undefined }}
    >
      {/* Top accent bar */}
      <div
        className={`h-1 w-full ${isGold ? 'bg-gradient-to-r from-gold-400 to-gold-600' : 'bg-gradient-to-r from-crimson-500 to-crimson-700'}`}
      />

      <div className="flex flex-col flex-1 p-7">
        {/* Number + badge */}
        <div className="flex items-start justify-between mb-5">
          <span
            className={`font-display font-bold leading-none select-none ${isGold ? 'text-gold-400' : 'text-crimson-200'}`}
            style={{ fontSize: '3.5rem' }}
            aria-hidden="true"
          >
            {resource.number}
          </span>
          <span
            className={`text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${isGold ? 'bg-gold-50 text-gold-700' : 'bg-crimson-50 text-crimson-600'}`}
          >
            Free Guide
          </span>
        </div>

        {/* Title */}
        <h2
          className="font-display font-bold text-base-900 leading-tight mb-2"
          style={{ fontSize: 'clamp(1.3rem, 2.2vw, 1.6rem)' }}
        >
          {resource.title}
        </h2>

        {/* Tagline */}
        <p
          className={`font-display italic text-[0.95rem] leading-snug mb-4 ${isGold ? 'text-gold-700' : 'text-crimson-600'}`}
        >
          {resource.tagline}
        </p>

        {/* Divider */}
        <div
          className={`w-8 h-[2.5px] rounded-full mb-4 ${isGold ? 'bg-gold-400' : 'bg-crimson-400'}`}
        />

        {/* Description */}
        <p className="text-[13.5px] text-base-600 leading-[1.85] mb-5">{resource.description}</p>

        {/* Highlights */}
        <ul className="space-y-2 mb-7 flex-1">
          {resource.highlights.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span
                className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${isGold ? 'bg-gold-400' : 'bg-crimson-400'}`}
              />
              <span className="text-[13px] text-base-600 leading-snug">{item}</span>
            </li>
          ))}
        </ul>

        {/* Download button */}
        <a
          href={resource.img}
          download={resource.filename}
          className={`inline-flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl text-[13.5px] font-semibold transition-all duration-200 ${
            isGold
              ? 'bg-gold-500 text-white hover:bg-gold-600 shadow-[0_4px_14px_-2px_rgba(232,160,32,0.4)] hover:shadow-[0_6px_18px_-2px_rgba(232,160,32,0.5)]'
              : 'bg-crimson-600 text-white hover:bg-crimson-700 shadow-[0_4px_14px_-2px_rgba(155,28,28,0.35)] hover:shadow-[0_6px_18px_-2px_rgba(155,28,28,0.45)]'
          }`}
        >
          <RiDownload2Line size={17} />
          Download Guide
        </a>
      </div>
    </div>
  )
}

export const HerJourneyHubCardsSection = () => {
  const { ref, inView } = useInView<HTMLDivElement>()

  return (
    <section id="resources" className="bg-base-50 py-20 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-14 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.22em] text-crimson-600 mb-4">
            Your Resource Library
          </span>
          <h2
            className="font-display font-bold text-base-900 leading-tight mb-4"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}
          >
            Six Guides for Every Stage
          </h2>
          <p className="text-base-500 text-[15px] leading-relaxed max-w-[520px] mx-auto">
            Download any guide for free — no sign-up required. Each one is packed with
            evidence-based information, practical tips, and words of encouragement.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {resources.map((resource, i) => (
            <ResourceCard key={resource.id} resource={resource} index={i} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-16 text-center ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.5s' }}
        >
          <div className="inline-flex flex-col items-center gap-5 bg-white border border-base-100 rounded-2xl px-8 py-8 shadow-[0_4px_24px_-4px_rgba(26,21,16,0.06)]">
            <p
              className="font-display italic text-base-700 leading-relaxed max-w-[480px]"
              style={{ fontSize: 'clamp(1rem, 1.8vw, 1.15rem)' }}
            >
              "These guides are a starting point. For personalised support on your unique journey,
              I'm here for you."
            </p>
            <p className="text-[12px] font-semibold text-base-500 uppercase tracking-[0.18em]">
              — Simone, H.E.R Birth Keeper
            </p>
            <Link to={ROUTES.ENQUIRY} className="btn-primary mt-1">
              Book Your Free Support Call
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
